import React, { useMemo, useState } from "react";
import { useToast } from "./Toast";
import { CONTACTO_INFO, MAP_ADDRESS } from "../data/content";
import InstagramIcon from "./icons/InstagramIcon";
import { isValidName, isValidPhone, isValidEmail, sanitizePhone } from "../utils/validation";

const INITIAL_VALUES = {
  nombre: "",
  telefono: "",
  correo: "",
  edadFestejado: "",
  fechaTentativa: "",
  mensaje: "",
};

function validateLead({ nombre, telefono, correo, edadFestejado, fechaTentativa }) {
  const errors = {};
  if (!isValidName(nombre)) {
    errors.nombre = "Escribe tu nombre completo";
  }
  if (!isValidPhone(telefono)) {
    errors.telefono = "Escribe solo números (10 a 15 dígitos)";
  }
  if (!isValidEmail(correo)) {
    errors.correo = "Escribe un correo válido";
  }
  if (!edadFestejado) {
    errors.edadFestejado = "Escribe la edad del festejado";
  } else {
    const edad = Number(edadFestejado);
    if (!Number.isInteger(edad) || edad < 1 || edad > 13) {
      errors.edadFestejado = "La edad debe ser entre 1 y 13";
    }
  }
  if (!fechaTentativa) {
    errors.fechaTentativa = "Elige una fecha tentativa";
  }
  return errors;
}

export default function Contacto() {
  const toast = useToast();
  const [values, setValues] = useState(INITIAL_VALUES);
  const [touched, setTouched] = useState({});
  const [sending, setSending] = useState(false);

  const errors = useMemo(() => validateLead(values), [values]);
  const canSubmit = Object.keys(errors).length === 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: name === "telefono" ? sanitizePhone(value) : value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const showError = (name) => touched[name] && errors[name];

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      setTouched({
        nombre: true,
        telefono: true,
        correo: true,
        edadFestejado: true,
        fechaTentativa: true,
      });
      toast("Revisa los campos marcados en rojo ✿");
      return;
    }

    setSending(true);
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origen: "contacto", ...values }),
      });
      if (!res.ok) throw new Error("bad status");
      toast("Mensaje enviado — te contactamos en menos de 24 hrs ✿");
      setValues(INITIAL_VALUES);
      setTouched({});
    } catch {
      toast("No pudimos enviar tu mensaje, intenta de nuevo en unos minutos ✿");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" className="band sage" data-screen-label="06 Contacto">
      <div className="band-bg" />
      <div className="scatter" />
      <div className="tear-top tear-sage-top" />

      <div className="wrap">
        <div className="sec-head">
          <h2 className="sec-title">Contáctanos</h2>
        </div>

        <div className="contact-grid">
          <form className="contact-card" onSubmit={onSubmit} noValidate>
            <h3>Cuéntanos de la fiesta</h3>
            <div className="sub">Mientras más detalles, mejor planeación</div>
            <div className="row2c">
              <div>
                <label className="field">Nombre</label>
                <input
                  className={"input" + (showError("nombre") ? " has-error" : "")}
                  name="nombre"
                  value={values.nombre}
                  onChange={handleChange}
                  placeholder="María González"
                />
                {showError("nombre") && <span className="field-error">{errors.nombre}</span>}
              </div>
              <div>
                <label className="field">Teléfono / WhatsApp</label>
                <input
                  className={"input" + (showError("telefono") ? " has-error" : "")}
                  name="telefono"
                  type="tel"
                  inputMode="numeric"
                  value={values.telefono}
                  onChange={handleChange}
                  placeholder="5610089073"
                />
                {showError("telefono") && <span className="field-error">{errors.telefono}</span>}
              </div>
            </div>
            <label className="field">Correo</label>
            <input
              className={"input" + (showError("correo") ? " has-error" : "")}
              name="correo"
              type="email"
              value={values.correo}
              onChange={handleChange}
              placeholder="maria@correo.mx"
            />
            {showError("correo") && <span className="field-error">{errors.correo}</span>}
            <div className="row2c">
              <div>
                <label className="field">Edad del festejado</label>
                <input
                  className={"input" + (showError("edadFestejado") ? " has-error" : "")}
                  name="edadFestejado"
                  type="number"
                  min="1"
                  max="13"
                  value={values.edadFestejado}
                  onChange={handleChange}
                  placeholder="6"
                />
                {showError("edadFestejado") && <span className="field-error">{errors.edadFestejado}</span>}
              </div>
              <div>
                <label className="field">Fecha tentativa</label>
                <input
                  className={"input" + (showError("fechaTentativa") ? " has-error" : "")}
                  name="fechaTentativa"
                  type="date"
                  value={values.fechaTentativa}
                  onChange={handleChange}
                />
                {showError("fechaTentativa") && <span className="field-error">{errors.fechaTentativa}</span>}
              </div>
            </div>
            <label className="field">¿Qué tienes en mente?</label>
            <textarea
              className="input"
              name="mensaje"
              value={values.mensaje}
              onChange={handleChange}
              placeholder="Queremos una fiesta de circo con piñata, magos y arroz con leche para 25 niños..."
            />
            <button className="btn" type="submit" style={{ marginTop: 14 }} disabled={sending || !canSubmit}>
              {sending ? (
                <>
                  <span className="btn-spinner" /> Enviando…
                </>
              ) : (
                "Enviar mensaje"
              )}
            </button>
          </form>

          <div className="contact-card">
            <h3>Ubicación</h3>
            <ul className="info-list">
              {CONTACTO_INFO.map((c, i) => (
                <li key={i}>
                  <div className={"ico " + c.cls}>
                    {c.ico === "instagram" ? <InstagramIcon /> : c.ico}
                  </div>
                  <div>
                    <span className="lbl">{c.lbl}</span>
                    <b dangerouslySetInnerHTML={{ __html: c.value }} />
                  </div>
                </li>
              ))}
            </ul>
            <div className="map-embed">
              <iframe
                title="Ubicación LoREMi G Party Hall"
                src={`https://www.google.com/maps?q=${encodeURIComponent(MAP_ADDRESS)}&output=embed`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <a
              className="map-link"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(MAP_ADDRESS)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ver en Google Maps ↗
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
